import { createAction, createReducer } from "redux-act";
import produce from "immer";
import { ExtendedProjectInfoDc, ProjectContentItemDc } from "@evergis/api";

import { reducerRegistry } from "../reducerRegistry";

import { LayerFetchStatus } from "../../resources/layerWithTable";
import { AsyncState } from "../../interfaces/redux";
import { SelectedFilters } from "../../modules/FeatureCard/types";
import { FeatureDataSource } from "../showFeature/types";

export type LayerMeta = {
  status?: LayerFetchStatus;
  condition?: string;
  lastCount?: number | null;
  count?: number | null;
  alias?: string;
  isVisible?: boolean;
  geometryAttribute?: string;
  type?: string;
  children?: ProjectContentItemDc[] | null;
};

export type Layers = {
  [key: string]: LayerMeta;
};

export type LayerMetaUpdate = {
  name: string;
} & LayerMeta;

export type ProjectState = AsyncState<{
  project?: ExtendedProjectInfoDc;
  layers: Layers;
  page?: number;
  dataSources?: FeatureDataSource[];
  dataSourceFilters?: SelectedFilters;
  dataSourcesAreLoading?: boolean;
  selectedTabId?: string;
  geometryFilter?: boolean;
  selectedLayers?: string[];
}>;

const reducerName = "project";

const initialState = {
  layers: {},
  dataSources: null,
  dataSourceFilters: {},
  dataSourcesAreLoading: false,
  page: 1,
  loading: true,
  error: null,
  geometryFilter: true,
  selectedLayers: [],
};

export const setProjectName = createAction<string>(`${reducerName}/set-project-name`);
export const setProject = createAction<ExtendedProjectInfoDc>(`${reducerName}/set-project`);
export const setLoading = createAction<boolean>(`${reducerName}/set-project-loading`);
export const setError = createAction<Error>(`${reducerName}/set-project-error`);
export const updateProject = createAction<Partial<ExtendedProjectInfoDc>>(`${reducerName}/update-project`);
export const reset = createAction(`${reducerName}/reset`);
export const createTemporaryProject = createAction(`${reducerName}/create-temporary-project`);
export const setLayersMeta = createAction<Layers>(`${reducerName}/set-layers-meta`);
export const setSelectedLayers = createAction<string[]>(`${reducerName}/set-selected-layers`);
export const updateLayerMeta = createAction<LayerMetaUpdate>(`${reducerName}/update-layer-meta`);
export const deleteLayerMeta = createAction<string>(`${reducerName}/delete-layer-meta`);
export const setProjectDataSources = createAction<FeatureDataSource[]>(`${reducerName}/set-data-sources`);
export const setProjectDataSourceFilters = createAction<SelectedFilters>(`${reducerName}/set-data-source-filters`);
export const setProjectDataSourcesAreLoading = createAction<boolean>(`${reducerName}/set-data-sources-are-loading`);
export const setSelectedTabId = createAction<string>(`${reducerName}/set-selected-tab-id`);
export const toggleGeometryFilter = createAction(`${reducerName}/set-geometry-filter`);
export const setPage = createAction<number>(`${reducerName}/set-page`);
export const nextPage = createAction<number>(`${reducerName}/next-page`);
export const prevPage = createAction<number>(`${reducerName}/prev-page`);
export const changePage = createAction<number>(`${reducerName}/change-page`);
export const reducer = createReducer({}, initialState);

reducer.on(
  setProject,
  produce((state: ProjectState, project: ExtendedProjectInfoDc) => {
    state.project = project;
    state.loading = false;
  }),
);

reducer.on(
  updateProject,
  produce((state: ProjectState, project: Partial<ExtendedProjectInfoDc>) => {
    if (!state.project) {
      return;
    }

    if (project.name && state.project.name !== project.name) {
      return;
    }

    state.loading = true;

    state.project = {
      ...state.project,
      ...project,
      content: {
        ...state.project.content,
        ...(project.content || {}),
      },
    };
  }),
);

reducer.on(
  setSelectedLayers,
  produce((state: ProjectState, selectedLayers: string[]) => {
    state.selectedLayers = selectedLayers;
  }),
);

reducer.on(
  setLayersMeta,
  produce((state: ProjectState, payload) => {
    state.layers = payload;
  }),
);

reducer.on(
  updateLayerMeta,
  produce((state: ProjectState, { name, children, ...newMeta }: LayerMetaUpdate) => {
    state.layers[name] = state.layers[name] ? { ...state.layers[name], ...newMeta } : newMeta;
  }),
);

reducer.on(
  deleteLayerMeta,
  produce((state: ProjectState, name: string) => {
    delete state.layers[name];
  }),
);

reducer.on(setProjectDataSources, (state, dataSources) => ({
  ...state,
  dataSources,
}));

reducer.on(setProjectDataSourceFilters, (state, dataSourceFilters) => ({
  ...state,
  dataSourceFilters,
}));

reducer.on(setProjectDataSourcesAreLoading, (state, dataSourcesAreLoading) => ({
  ...state,
  dataSourcesAreLoading,
}));

reducer.on(setSelectedTabId, (state, selectedTabId) => ({
  ...state,
  selectedTabId,
}));

reducer.on(toggleGeometryFilter, state => ({
  ...state,
  geometryFilter: !state.geometryFilter,
}));

reducer.on(
  setPage,
  produce((state: ProjectState, payload) => {
    state.page = payload;
  }),
);

reducer.on(nextPage, (state, total) => ({
  ...state,
  page: state.page === total ? 1 : state.page + 1,
}));

reducer.on(prevPage, (state, total) => ({
  ...state,
  page: state.page === 1 ? total : state.page - 1,
}));

reducer.on(changePage, (state, page) => ({
  ...state,
  page,
}));

reducer.on(reset, () => initialState);
reducerRegistry.register(reducerName, reducer);
